/**
 * @file Table feature: select rows.
 * @since 3.7.0
 */

import { createFromMarkup } from './utils';
import { Dropdown } from '../../dropdown/Dropdown';
import TableAbstractFeature from './TableAbstractFeature';

/**
 * Class representing Table Select feature.
 */
export default class TableSelect extends TableAbstractFeature {
  constructor(table) {
    super(table);
    this.selectType = this.component.settings.selectable;

    this.listeners = {
      clickBodyRowSingle: (evt) => {
        if (evt.target.tagName === 'TD') {
          const clickedRow = evt.target.closest('tr');

          if (clickedRow && clickedRow.classList.contains('selected')) {
            this._unselectRow(clickedRow);
          } else {
            // Unselect displayed rows
            const selectedRow = this.dom.table.querySelector('tr.selected');
            if (selectedRow) {
              this._unselectRow(selectedRow);
            }
            // Unselect all data that is not displayed
            this.component.onDeselectAll();
            this._selectRow(clickedRow);
          }
        }
      },
      clickBodyRowMulti: (evt) => {
        this._toggleRow(evt);
        this._setMasterCheckboxState();
      },
      clickMasterCheckbox: () => {
        this._toggleAllCheckboxes();
        this._setMasterCheckboxState();
      },
      clickMasterDropdown: (evt) => {
        if (evt.target.classList.contains('item')) {
          const val = evt.target.dataset.value;
          if (val === 'all') {
            this.masterCheckbox.checked = true;
            this.component.onSelectAll();
          }

          if (val === 'none') {
            this.masterCheckbox.checked = false;
            this.component.onDeselectAll();
          }
          this._toggleAllCheckboxes();
          this._setMasterCheckboxState();
        }
      }
    };
  }

  init() {
    if (this.selectType === 'multi') {
      this.dom.table.classList.add('selectable');
    }

    this._addEventListeners();
  }

  destroy() {
    this._removeEventListeners();
  }

  _addEventListeners() {
    if (this.selectType === 'single') {
      this.dom.table.addEventListener('click', this.listeners.clickBodyRowSingle);
    }
  }

  _removeEventListeners() {
    if (this.selectType === 'single') {
      this.dom.table.removeEventListener('click', this.listeners.clickBodyRowSingle);
    }
  }

  /**
   * Create master checkbox. Click on the checkbox will toggle all rows on the current page.
   * @private
   */
  _renderMasterCheckbox() {
    const row = this.dom.thead.querySelector('tr');
    const th = document.createElement('th');

    th.classList.add('cell-select');
    this.masterCheckbox = document.createElement('input');

    this.masterCheckbox.setAttribute('type', 'checkbox');
    this.masterCheckbox.id = `${this.dom.table.id}-master`;

    const label = document.createElement('label');
    label.classList.add('checkbox', 'select-row');
    label.setAttribute('for', `${this.dom.table.id}-master`);

    th.append(this.masterCheckbox);
    th.append(label);

    if (this.dom.tablePagination) {
      this._renderCheckboxDropdown(th);
      th.classList.add('wide');
    }

    row.insertBefore(th, row.firstChild);

    this.masterCheckbox.addEventListener('change', this.listeners.clickMasterCheckbox);

    this._setMasterCheckboxState();
  }

  /**
   * Create dropdown with advanced master select options.
   * @private
   * @param {HTMLElement} th
   */
  _renderCheckboxDropdown(th) {
    const markup = `<div class="dropdown more" data-type="click">
      <button class="btn clickable"><i class="icon icon-more"></i></button>
      <div class="menu start">
        <div class="item" data-value="all">Select items from all pages</div>
        <div class="item" data-value="none">Deselect items from all pages</div>
      </div>
    </div>`;

    this.masterCheckboxDropdown = createFromMarkup(markup);
    th.append(this.masterCheckboxDropdown);

    const dropdown = new Dropdown(this.masterCheckboxDropdown);
    dropdown.init();

    this.masterCheckboxDropdown.querySelector('.menu').addEventListener('click', this.listeners.clickMasterDropdown);

    this._setMasterCheckboxDropdownState();
  }

  /**
   * Create td and checkboxes in a row.
   * @private
   */
  _renderSelectTd(row, data, index) {
    const td = document.createElement('td');
    td.classList.add('cell-select');

    if (!row.classList.contains('details-row')) {
      this._createCheckbox(td, `${this.dom.table.id}-row${index}`, data.selected);
    }

    row.insertBefore(td, row.firstChild);
  }

  /**
   * Create input checkbox and label
   * @private
   * @param {HTMLElement} cell
   * @param {string} id
   */
  _createCheckbox(cell, id, checked = false) {
    const input = document.createElement('input');
    input.setAttribute('type', 'checkbox');
    input.id = id;
    input.checked = checked;

    const label = document.createElement('label');
    label.classList.add('checkbox', 'select-row');
    label.setAttribute('for', id);

    cell.append(input);
    cell.append(label);

    input.addEventListener('change', this.listeners.clickBodyRowMulti);
  }

  /**
   * Set master checkbox state.
   * @private
   */
  _setMasterCheckboxState() {
    if (!this.masterCheckbox) return;

    const numBodyRows = this._getNumberOfRows();
    const numCheckedRows = this._getNumberOfSelectedRows();
    this.masterCheckbox.checked = numCheckedRows > 0;
    this.masterCheckbox.indeterminate = numCheckedRows > 0 && numCheckedRows < numBodyRows;
    this.masterCheckbox.toggleAttribute('disabled', this.component.data.length === 0);

    this._setMasterCheckboxDropdownState();
  }

  /**
   * Hide / show dropdown items depending on selected rows
   * @private
   */
  _setMasterCheckboxDropdownState() {
    if (!this.masterCheckboxDropdown) return;

    this.masterCheckboxDropdown.querySelector('button').toggleAttribute('disabled', this.component.data.length === 0);

    const hideSelectAll = (this.component.selected.length === this.component.data.length);
    const hideDeselectAll = (this.component.selected.length === 0);

    this.masterCheckboxDropdown.querySelector('.item[data-value="all"]').classList.toggle('disabled', hideSelectAll);
    this.masterCheckboxDropdown.querySelector('.item[data-value="none"]').classList.toggle('disabled', hideDeselectAll);
  }

  /**
   * Toggle all checkboxes.
   * @private
   */
  _toggleAllCheckboxes() {
    this.dom.bodyRows.forEach(row => {
      if (this.masterCheckbox.checked) {
        this._selectRow(row);
      } else {
        this._unselectRow(row);
      }
    });
  }

  /**
   * Toggle row.
   * @private
   * @param {Event} evt - Click event.
   * @param {HTMLElement} evt.target - Clicked element (tr or td with checkbox).
   */
  _toggleRow({target}) {
    const row = target.closest('tr');
    if (row.classList.contains('selected')) {
      this._unselectRow(row);
    } else {
      this._selectRow(row);
    }
  }

  /**
   * Select row.
   * @private
   * @param {HTMLElement} row - table row.
   */
  _selectRow(row) {
    row.classList.add('selected');

    // Check checkbox in multi select
    if (row.querySelector('td.cell-select input')) {
      row.querySelector('td.cell-select input').checked = true;
    }

    this.component.onToggleSelectRow(row, true);
  }

  /**
   * Unselect row.
   * @private
   * @param {HTMLElement} row - table row.
   */
  _unselectRow(row) {
    row.classList.remove('selected');

    // Uncheck checkbox in multi select
    if (row.querySelector('td.cell-select input')) {
      row.querySelector('td.cell-select input').checked = false;
    }

    this.component.onToggleSelectRow(row, false);
  }

  /**
   * Get total number of rows.
   * @private
   */
  _getNumberOfRows() {
    return this.dom.bodyRows.length;
  }

  /**
   * Get number of selected rows.
   * @private
   */
  _getNumberOfSelectedRows() {
    return this.dom.table.querySelectorAll('tbody input[type="checkbox"]:checked').length;
  }

  /**
   * From observer Table.js
   */

  beforeCreatedHead() {
    if (this.masterCheckbox) {
      this.masterCheckbox.removeEventListener('change', this.listeners.clickMasterCheckbox);
    }

    if (this.masterCheckboxDropdown) {
      this.masterCheckboxDropdown.removeEventListener('click', this.listeners.clickMasterDropdown);
    }
  }

  onCreatedHead() {
    if (this.selectType === 'multi') {
      this._renderMasterCheckbox();
    }
  }

  beforeCreatedBody() {
    if (this.selectType === 'multi') {
      // Remove all checkboxes listeners
      this.dom.bodyRows.forEach((bodyRow) => {
        bodyRow.querySelector('td.cell-select input').removeEventListener('change', this.listeners.clickBodyRowSingle);
      });
    }
  }

  onCreatedBody() {
    if (this.selectType === 'multi') {
      this._setMasterCheckboxState();
    }
  }

  onCreatedRow(tr, trData, index) {
    tr.classList.toggle('selected', trData.selected);

    if (this.selectType === 'multi') {
      this._renderSelectTd(tr, trData, index);
    }
  }

  onCreatedDetailsRow(td) {
    if (this.selectType === 'multi') {
      this._renderSelectTd(td.closest('tr.details-row'));
    }
  }
}
